import React from "react";
import {
  X,
  Calendar,
  Users,
  MapPin,
  CheckCircle,
  Globe,
  Info,
} from "lucide-react";
import moment from "moment";
import { useTranslation } from "react-i18next";

export default function BossStatisticByDateShowDataModal({
  visible,
  onClose,
  type,
  data = [],
  monthName,
}) {
  const { t } = useTranslation();
  if (!visible) return null;

  // ── Content renderers ─────────────────────────────────────────────────────

  const renderActiveDays = () => (
    <>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
          <Calendar className="w-6 h-6 text-blue-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{t("workingDays")}</h3>
      </div>
      <p className="text-sm text-gray-500 mb-5">{t("workDays")}</p>

      <div className="space-y-3">
        {data.map((date, index) => (
          <div
            key={index}
            className="flex items-center gap-3 bg-gray-50 rounded-2xl p-4"
          >
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
              <span className="text-xl font-bold text-blue-500">
                {moment(date, "DD-MM-YYYY").format("DD")}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold text-gray-900">
                {moment(date, "DD-MM-YYYY").format("dddd")}
              </p>
              <p className="text-sm text-gray-500">
                {moment(date, "DD-MM-YYYY").format("MMM DD, YYYY")}
              </p>
            </div>
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-4 h-4 text-green-500" />
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const renderEmployees = () => (
    <>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center">
          <Users className="w-6 h-6 text-purple-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{t("employees")}</h3>
      </div>
      <p className="text-sm text-gray-500 mb-5">{t("employees")}</p>

      <div className="space-y-3">
        {data.map((employee) => (
          <div
            key={employee.id}
            className="flex items-center gap-3 bg-gray-50 rounded-2xl p-4"
          >
            <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
              <span className="text-xl font-bold text-purple-600">
                {employee.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold text-gray-900 truncate">
                {employee.name}
              </p>
              <p className="text-sm text-gray-500">
                {employee.workDays}{" "}
                {employee.workDays === 1 ? t("day") : t("workDays")}
              </p>
            </div>
            {employee.monthlySalary && (
              <span className="bg-purple-50 text-purple-600 text-xs font-semibold px-3 py-1.5 rounded-xl flex-shrink-0">
                {t("monthly")}
              </span>
            )}
          </div>
        ))}
      </div>
    </>
  );

  const renderPlaces = () => (
    <>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-full bg-yellow-50 flex items-center justify-center">
          <MapPin className="w-6 h-6 text-yellow-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{t("place")}</h3>
      </div>
      <p className="text-sm text-gray-500 mb-5">{t("place")}</p>

      <div className="space-y-3">
        {data.map((place) => {
          // Profit display logic — identical to RN
          let profitDisplay;
          let profitLabel;

          if (place.paidWay === 3) {
            profitDisplay = place.profit;
            profitLabel = "Global Profit";
          } else if (place.paidWay === 2) {
            profitDisplay = place.employeeCount
              ? place.profit * place.employeeCount
              : place.profit;
            profitLabel =
              place.employeeCount > 1
                ? `Profit (${place.employeeCount} employees)`
                : "Profit";
          } else {
            profitDisplay = place.profit;
            profitLabel = "Profit";
          }

          const paymentBadge =
            place.paidWay === 1
              ? {
                  label: t("monthly"),
                  bg: "bg-purple-50",
                  text: "text-purple-600",
                }
              : place.paidWay === 2
                ? { label: t("daily"), bg: "bg-green-50", text: "text-green-600" }
                : { label: "Global", bg: "bg-blue-50", text: "text-blue-600" };

          return (
            <div
              key={place.id}
              className="bg-gray-50 rounded-2xl p-4 space-y-2"
            >
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-yellow-50 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-yellow-500" />
                </div>

                <div className="flex-1 min-w-0">
                  {/* Place name + payment type badge */}
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-base font-semibold text-gray-900 truncate">
                      {place.place}
                    </p>
                    {place.paidWay && (
                      <span
                        className={`text-[10px] font-semibold px-2 py-1 rounded-lg ${paymentBadge.bg} ${paymentBadge.text}`}
                      >
                        {paymentBadge.label}
                      </span>
                    )}
                  </div>

                  {/* Profit / daily salary */}
                  <div className="flex items-center gap-2 flex-wrap text-sm text-gray-500">
                    {place.paidWay === 2 && place.placeDailySalary ? (
                      <span>
                        ₪{place.placeDailySalary.toLocaleString()} Daily Salary
                      </span>
                    ) : (
                      <span
                        className={
                          place.paidWay === 3 ? "italic text-blue-500" : ""
                        }
                      >
                        ₪{(profitDisplay || 0).toLocaleString()} {profitLabel}
                      </span>
                    )}
                    <span className="text-gray-300">•</span>
                    <span>{paymentBadge.label}</span>
                  </div>

                  {/* Global note */}
                  {place.paidWay === 3 && (
                    <div className="flex items-center gap-1.5 mt-2 bg-blue-50 rounded-lg px-2.5 py-1.5">
                      <Info className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                      <p className="text-[10px] text-blue-600 leading-snug">
                        Global profit is tracked via total payments, not monthly
                        breakdown.
                      </p>
                    </div>
                  )}
                </div>

                {/* Work days badge */}
                <span className="bg-yellow-100 text-yellow-600 text-xs font-semibold px-3 py-1.5 rounded-xl flex-shrink-0 whitespace-nowrap">
                  {place.workCount} {place.workCount === 1 ? t("day") : t("workDays")}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderContent = () => {
    switch (type) {
      case "activeDays":
        return renderActiveDays();
      case "employees":
        return renderEmployees();
      case "places":
        return renderPlaces();
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className="relative bg-white rounded-t-3xl w-full max-w-2xl flex flex-col animate-slide-up"
        style={{ height: "85vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Fixed header ── */}
        <div className="flex items-center justify-between px-5 py-5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{monthName}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {data.length} {type === "activeDays" ? "days" : "total"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* ── Scrollable content ── */}
        <div className="flex-1 overflow-y-auto px-5 py-5 pb-16">
          {renderContent()}
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </div>
  );
}
